/**
 * Pure-data English catalogue for namespace 'rpe' (W1-07a formats-json-rpe; imports only core/util/types).
 *
 *  - report.*  normalisation report entries of `normalizeRpe` (classes 'normalized' / 'changed'); params named in
 *              braces; the entry's `count` and `path` are shown by the import report next to the text;
 *  - kept.*    flagged / RPE-only rows of `rpeKeptReport` (imported exactly, but worth knowing);
 *  - diag.*    decoder diagnostics as the Raw panel shows them (`diagnosticRef`): params `path`, `detail` (a raw
 *              source lexeme or value, never English text), `expected` and `found` (kind codes, translated here
 *              with ICU `select`; '' when unknown).
 */

import type { MessageCatalog } from '../../core/util/types';

/** What a value should have been (RpeDiagnostic `expected`). */
const EXPECTED =
  '{expected, select,' +
  ' wholeNumber {a whole number}' +
  ' number {a number}' +
  ' text {text}' +
  ' bool {true or false}' +
  ' list {a list}' +
  ' object {an object}' +
  ' beat {a beat [a, b, c] of three whole numbers}' +
  ' colour {a colour [r, g, b] of three whole numbers from 0 to 255}' +
  ' bezier4 {four numbers [x1, y1, x2, y2]}' +
  ' xy {two numbers [x, y]}' +
  ' event {an event object}' +
  ' note {a note object}' +
  ' line {a judge line object}' +
  ' layer {a layer object or null}' +
  ' bpm {a BPM object}' +
  ' controlPoint {a control point object}' +
  ' chart {an object with META, BPMList and judgeLineList}' +
  ' eventList {a list of events}' +
  ' noteList {a list of notes}' +
  ' controlList {a list of control points}' +
  ' timeTag {an object with name and time}' +
  ' other {another kind of value}}';

/** The JSON kind found instead (RpeDiagnostic `found`). */
const FOUND =
  '{found, select,' +
  ' null {null}' +
  ' bool {true or false}' +
  ' num {a number}' +
  ' str {text}' +
  ' arr {a list}' +
  ' obj {an object}' +
  ' other {another kind of value}}';

const catalog: MessageCatalog = {
  ns: 'rpe',
  locale: 'en',
  messages: {
    // ---- File-level problems (Phira would not load the file) ----
    'report.invalidUtf8':
      'The chart file is not valid UTF-8 text. It was read anyway; Phira would not recognise it as an RPE chart.',
    'report.bom':
      'The chart file starts with a byte order mark (BOM). It was removed; Phira cannot load a chart that has one.',
    'report.leadingWhitespace':
      'The chart file starts with spaces or empty lines. They were removed; without them Phira detects the format reliably.',
    'report.noMetaSubstring':
      'The chart has no "META" section, so Phira would not recognise it as an RPE chart. Default chart information was used.',

    // ---- Field problems (Phira would refuse to load the chart) ----
    'report.floatInIntField':
      'Whole numbers were written with a decimal point in "{field}" (for example 1.0). They were rounded; Phira refuses such charts.',
    'report.missingRequired': 'The required field "{field}" was missing and was filled with the RPE default.',
    'report.duplicateKey': 'The field "{field}" was written twice; the last value was used. Phira refuses such charts.',
    'report.wrongType': 'The field "{field}" has the wrong kind of value and was replaced by its default.',
    'report.outOfRange':
      'The whole number in "{field}" is outside the range this field allows. It was corrected; Phira refuses such charts.',
    'report.numberOverflow':
      'A number in "{field}" is too large to be read and was replaced. Phira refuses such charts.',
    'report.numberOverflowIgnored':
      'A number in "{field}" is too large to be stored and was replaced. Phira does not read this value.',
    'report.negativeUnsigned':
      'The field "{field}" cannot be negative. The value was corrected; Phira refuses such charts.',
    'report.tripleZeroDen': 'A beat in "{field}" divides by zero.',
    'report.unknownAttachUI':
      'The UI attachment {value} is not one of pause, combonumber, combo, score, bar, name or level. It was converted or removed; Phira refuses such charts.',
    'report.controlMissingValueKey':
      'A note control point has no "{field}" value. It was set to 1; Phira would crash while loading this chart.',
    'report.controlNonNumericExtra':
      'A note control point has a non-number value in "{field}". It was removed; Phira refuses such charts.',
    'report.controlValueClamped':
      'A note control point has a number in "{field}" that is too large for Phira, which reads it as infinite. It was set to the largest number Phira can hold.',
    'report.valueClamped':
      'A number in "{field}" is too large for Phira, which reads it as infinite. It was set to the largest number Phira can hold.',
    'report.unpairedSurrogate':
      'The text in "{field}" contains half of a character written as a \\u escape (an unpaired surrogate). It was replaced by the character U+FFFD; Phira refuses such charts.',
    'report.unpairedSurrogateIgnored':
      'The text in "{field}" contains half of a character written as a \\u escape (an unpaired surrogate). It was replaced by the character U+FFFD, because it cannot be saved; Phira still loads the chart.',
    'report.rpeOnlyValue': 'The RPE-only field "{field}" has an unexpected value and was reset. Phira does not use it.',
    'report.metaInfoValue':
      'The chart information "{field}" is not text and was cleared. Phira still loads the chart; it only uses this field to fill in the song details when the package has no info.yml.',
    'report.notKept':
      'An RPE time tag has the field "{field}", which this editor cannot keep. It was removed; Phira does not use time tags.',
    'report.rpeVersionReplaced':
      'The RPE version is not a whole number or numeric text. It was replaced by 160, the value Phira uses for it.',

    // ---- Tempo ----
    'report.bpmInvalidTime': 'A BPM change has an invalid beat (a zero, negative or fractional part) and was removed.',
    'report.bpmInvalid': 'A BPM change with the tempo {bpm} was removed: tempos must be greater than 0.',
    'report.bpmEmpty':
      'The chart has no valid BPM. 120 BPM from beat 0 was used; Phira would crash with an empty BPM list.',
    'report.bpmUnsorted': 'The BPM changes were not in time order. They were sorted; timing is unchanged.',
    'report.bpmDuplicate':
      'Several BPM changes are at the same beat. Only the last one was kept: Phira uses it and ignores the others, so timing is unchanged.',
    'report.bpmUnsortedChanged':
      'The BPM changes were not in time order. They were sorted, which changes the timing compared to Phira.',
    'report.bpmShifted':
      'The first BPM change was at beat {beat}. Every beat of the chart was moved so that it starts at beat 0; timing is unchanged.',
    'report.bpmBeatOutOfRange':
      'BPM changes were removed: after moving the chart to start at beat 0, their beats are beyond the largest beat a chart can hold (or need a finer fraction than a beat can hold).',

    // ---- Event lists ----
    'report.eventInvalidTime':
      'Events with an invalid beat (a zero, negative or fractional part) were removed. Phira showed broken values for them.',
    'report.eventBeatOutOfRange':
      'Events were removed: after moving the chart to start at beat 0 at its first BPM change, their beats are beyond the largest or smallest beat a chart can hold (or need a finer fraction than a beat can hold).',
    'report.jumpAtRangeEnd':
      'A jump at the last beat a chart can hold was removed: no event can start there, because there is no later beat for it to end on. Phira showed the jumped-to value from there on.',
    'report.unsortedSame':
      'Events were not in time order. They were sorted; the motion looks exactly the same in Phira.',
    'report.unsortedChanged':
      'Events were not in time order. They were sorted; Phira played them in a way that depended on seeking, so the motion now differs.',
    'report.speedUnsorted':
      'Scroll speed events were not in time order. They were sorted; Phira computed different note positions for them.',
    'report.speedUnsorted170':
      'Scroll speed events were not in time order. They were sorted, as Phira does with RPE 1.7 speed curves.',
    'report.overlapSliced':
      'Overlapping events were trimmed: each later event now starts where the earlier one ends, on its own curve, as Phira plays it.',
    'report.overlapApprox':
      'Overlapping events were trimmed where Phira switches to them. The trimmed curve could only be matched approximately.',
    'report.curveNoNumber':
      'An overlapping event was trimmed, but its easing range is so narrow that Phira cannot compute its curve (it divides by zero), so Phira shows no proper value during it. It was kept with its own easing from where Phira switches to it; widen the easing range or pick another easing.',
    'report.eventsAsJumps':
      'Events with no length, ending before they start or hidden inside another event were turned into jumps, as Phira plays them.',
    'report.firstEventZeroLength':
      'The first event has no length. Phira showed invalid values before beat {beat}; the value now starts there.',
    'report.defaultKept':
      'The first event of this list now starts at beat 0, so Phira would no longer show the default value (white, empty text, no tilt or no brush) at the very start of the chart. A short event with that default value was added before beat 0, so the chart looks the same.',
    'report.startCut':
      'The first event of this list started before the first BPM change. It now starts at beat 0, cut on its own curve, so that Phira does not show the default value at the start of the chart.',
    'report.startCutApprox':
      'The first event of this list started before the first BPM change. It now starts at beat 0; its cut curve could only be matched approximately.',
    'report.leadInFilled':
      'Before the first event Phira continued its curve backwards. After moving the chart to start at beat 0 Phira would show the default value there instead, so an event reproducing that start was added from beat 0.',
    'report.defaultNotKept':
      'The first event of this list now starts at beat 0, so Phira no longer shows the default value (white, empty text, no tilt or no brush) at the very start of the chart. The event is at the smallest beat a chart can hold, so no event with the default value fits before it.',
    'report.defaultAtStart':
      'Moving the chart to start at beat 0 changes the very start of this list: Phira now shows its default value (white, empty text, no tilt or no brush) there instead of the original value.',
    'report.firstSpeedZeroLength':
      'The first scroll speed event has no length at the start of the chart. Phira could not place the notes of this line at all.',
    'report.speedCompressed':
      'Overlapping scroll speed events now start where the previous one ends, as Phira does with RPE 1.7 speed curves.',
    'report.bezierFlag':
      'An event turns its Bezier curve on with a value other than 1. Phira only checks for a value other than 0, so it was written as 1; the motion is unchanged.',
    'report.speedBezier':
      'Scroll speed events had a custom curve that Phira does not use for them. The curve was removed (scroll speed never carries one, so switching speed modes cannot crash Phira); an imported chart keeps it in phiedit.json.',
    'report.speedBezierCrash':
      'Scroll speed events use a custom curve, which makes Phira crash with RPE 1.7 speed curves. The curve was removed, so the speed now follows the easing type; an imported chart keeps it in phiedit.json.',
    'report.speedBezierShared':
      'Scroll speed events use a custom curve. With RPE 1.7 speed curves Phira takes that curve from another event with a similar curve. The curve was removed, so the speed now follows the easing type and note positions differ; an imported chart keeps it in phiedit.json.',
    'report.gifUnsorted':
      'GIF progress events were not in time order. They were sorted; Phira played them in a way that depended on their order, so the animation can differ.',
    'report.gifJumpHidden':
      'GIF progress events with no length, or hidden inside another event, were removed. The next event starts at the same moment, so Phira never showed them.',
    'report.gifJumpDropped':
      'GIF progress jumps were removed (the first at beat {beat}). An event with no length, or hidden inside another event, makes Phira jump to its end value and play on at the GIF speed. No event can do that without stopping the GIF, so the GIF now plays on from its previous progress there.',
    'report.inclineRebuilt':
      'Note tilt events were reordered or trimmed. Phira only uses the tilt at the start of the chart.',
    'report.textCounterJump':
      'A jump to a counting text (%P%) was added. Phira shows such text unformatted after its last event, so the text can briefly look different.',
    'report.textCounterHold':
      'A counting text (%P%) is now shown unformatted earlier than in Phira: Phira showed it counted until a later event that was removed (it had no length or repeated the same text), and unformatted only after that event.',
    'report.emptyFirstLayerAlpha':
      'An empty opacity list in the first layer made Phira ignore the other layers and show the line fully opaque. It is now a constant opacity of 255; the ignored layers are kept in phiedit.json. Their event times and custom curves no longer count for when notes stop moving at the end of the chart (max_time) or for the curves Phira shares between events.',
    'report.emptyFirstLayer':
      'An empty list in the first layer made Phira ignore this property on the other layers. It was removed; the ignored layers are kept in phiedit.json. Their event times and custom curves no longer count for when notes stop moving at the end of the chart (max_time) or for the curves Phira shares between events.',
    'report.emptyLaterLayer': 'An empty event list in a later layer makes Phira crash. It was removed.',
    'report.emptyTextScale':
      'An empty text list became a line with empty text. This changes the length of the text: Phira halves it only while the list is empty.',
    'report.gifOnNonGif':
      'GIF progress events are set on the texture "{texture}", which is not a GIF. Phira cannot load this chart. The events were removed and kept in phiedit.json.',
    'report.timeTagInvalid': 'An RPE time tag with an invalid beat was removed. Phira does not use time tags.',
    'report.timeTagBeatOutOfRange':
      'An RPE time tag was removed: after moving the chart to start at beat 0, its beat is beyond what a chart can hold. Phira does not use time tags.',

    // ---- Notes and lines ----
    'report.noteInvalidTime': 'Notes with an invalid beat (a zero, negative or fractional part) were removed.',
    'report.noteBeatOutOfRange':
      'Notes were removed: after moving the chart to start at beat 0 at its first BPM change, their beats are beyond the largest or smallest beat a chart can hold (or need a finer fraction than a beat can hold).',
    'report.unknownNoteType': 'A note has the unknown type {type}. It became a Tap; Phira refuses such charts.',
    'report.holdReversed': 'A Hold ended before it started. It now ends where it starts.',
    'report.fatherInvalid':
      'A line names the parent {father}, which does not exist. The parent was removed; Phira would crash.',
    'report.fatherCycle':
      'Lines name each other as parents in a loop. One parent link was removed; Phira refuses such charts.',

    // ---- Kept exactly, but worth knowing ----
    'kept.leadIn': 'The first event starts after beat 0. Before it Phira continues its curve backwards; kept as it is.',
    'kept.speedBezierLegacy':
      'Scroll speed events have custom curves. Phira ignores them and plays speed as straight ramps. They are written back without the curve, which is kept in phiedit.json, because Phira would crash on them once RPE 1.7 speed curves are switched on.',
    'kept.easedSpeedLegacy':
      'Scroll speed events use curves, but Phira plays speed as straight ramps unless RPE 1.7 speed curves are switched on.',
    'kept.inclineAnimated': 'Note tilt changes over time, but Phira only uses its value at the start of the chart.',
    'kept.ignoredList': 'Phira does not draw "{list}" on this kind of line. The events are kept for RPE.',
    'kept.bezierHandles': 'Custom curves have handles outside the 0 to 1 range. They are kept as they are.',
    'kept.inertControl':
      'The note control "{curve}" has two points and starts at 1 with a straight curve, so Phira ignores it completely.',
    'kept.attachUiNotes': 'This line is attached to a UI element, so Phira hides its notes (they are still judged).',
    'kept.attachUiTexture':
      'This line is attached to a UI element but uses a texture, which makes Phira draw the element tiny.',
    'kept.zeroLengthHold': 'Holds with no length are kept. Phira counts and judges them and shows them briefly.',
    'kept.isFakeRaw': 'Notes use fake values other than 0 and 1. Phira treats them as fake, RPE as real.',
    'kept.noteColor': 'Notes carry an RPE test-build "color" field. Phira ignores it; it is kept for RPE.',
    'kept.bpmfactor': 'This line has a BPM factor of {value}. RPE uses it, Phira does not, so the two disagree.',
    'kept.anchor': 'This texture line has a custom anchor. RPE uses it, Phira always centres textures.',
    'kept.skewControl': 'This line has a note skew control. RPE uses it, Phira does not.',
    'kept.unknownKeys': 'The chart has fields this editor does not know. They are kept and written back unchanged.',

    // ---- Decoder diagnostics (Raw panel) ----
    'diag.floatInIntField': '{path}: write a whole number without a decimal point ({detail}).',
    'diag.missingRequired': '{path}: this field is required.',
    'diag.wrongType': `{path}: expected ${EXPECTED}, found ${FOUND}.`,
    'diag.wrongTypeValue': `{path}: expected ${EXPECTED}, found ${FOUND} ({detail}).`,
    'diag.duplicateKey': '{path}: this field is written twice; Phira refuses that.',
    'diag.outOfRange': '{path}: {detail} is outside the range of whole numbers this field allows.',
    'diag.numberOverflow': '{path}: {detail} is too large to be read as a number.',
    'diag.otherFragment': '{path}: this part is edited in its own section and is ignored here.',
    'diag.unpairedSurrogate':
      '{path}: the text contains half of a character written as a \\u escape (an unpaired surrogate); it cannot be saved and Phira refuses it where it reads the text.',
    'diag.notKept': '{path}: this field cannot be kept and is removed.',
    'diag.negativeUnsigned': '{path}: this value cannot be negative ({detail}).',
    'diag.tripleZeroDen': '{path}: the last number of a beat cannot be 0 ({detail}).',
    'diag.bom': 'The text starts with a byte order mark (BOM).',
    'diag.leadingWhitespace': 'The text starts with spaces or empty lines.',
    'diag.invalidUtf8': 'The file is not valid UTF-8 text.',
    'diag.noMetaSubstring': 'The text does not contain "META".',
    'diag.unknownAttachUI': '{path}: use one of pause, combonumber, combo, score, bar, name or level ({detail}).',
    'diag.unknownAttachUIKind': `{path}: use one of pause, combonumber, combo, score, bar, name or level (found ${FOUND}).`,
    'diag.controlMissingValueKey': '{path}: every control point needs its value ({detail}).',
    'diag.controlNonNumericExtra': `{path}: control points can only hold numbers (found ${FOUND}).`,
    'diag.controlNonNumericExtraValue': `{path}: control points can only hold numbers (found ${FOUND}: {detail}).`,
  },
};

export default catalog;
